import { debounce, flatten, last } from "lodash-es";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@telegram-apps/telegram-ui";
import { InviteRecord } from "~/types/api/dex/rebate";
import useAsyncAction from "~/hook/useAsyncAction";
import randomIcon from "~/components/RandomIcon";
import { formatDateOnly } from "~/utils/format";
import { formatPrice } from "~/utils/number-format";
import LottieSticker from "~/components/LottieSticker";
import SortedColumnHead from "~/components/SortedColumnHead/SortedColumnHead";
import { ColumnSortType } from "~/pages/constants";

type InviteListProps = {
    inviteList: (InviteRecord[] | null)[] | undefined;
    inviteListPageNum: number;
    setInviteListPageNum: (
        size: number
    ) => Promise<(InviteRecord[] | null)[] | undefined>;
    setInviteListSortType: React.Dispatch<React.SetStateAction<0 | 1>>;
};

const isNotNull = (item: InviteRecord | null): item is InviteRecord => {
    return item != null;
};

const InviteList: React.FC<InviteListProps> = ({
    inviteList,
    inviteListPageNum,
    setInviteListPageNum,
    setInviteListSortType,
}) => {
    const { t } = useTranslation();
    const wholeInviteList = flatten(inviteList);
    const lastPage = last(inviteList);

    const loadMoreFn = useCallback(async () => {
        return setInviteListPageNum(inviteListPageNum + 1);
    }, [inviteListPageNum, setInviteListPageNum]);

    const loadMoreAction = useAsyncAction(loadMoreFn);
    const dataList = wholeInviteList.filter(isNotNull);

    useEffect(() => {
        const scrollableContainer = document.getElementById("tg-main-content");
        const onScroll = debounce(() => {
            // refetch when last page is not empty
            const hasMore = lastPage && lastPage.length >= 0;
            const debouncedLoadMore = debounce(() => {
                loadMoreAction.run();
            }, 200);
            if (
                scrollableContainer &&
                scrollableContainer.scrollTop +
                    scrollableContainer.clientHeight >=
                    scrollableContainer.scrollHeight * 0.9 &&
                hasMore &&
                !loadMoreAction.isLoading
            ) {
                debouncedLoadMore();
            }
        }, 200);

        scrollableContainer?.addEventListener("scroll", onScroll);

        return () => {
            scrollableContainer?.removeEventListener("scroll", onScroll);
        };
    }, [lastPage, loadMoreAction]);

    const [friendsSortType, setFriendsSortType] = useState(ColumnSortType.None);
    const [rewardSortType, setRewardSortType] = useState(ColumnSortType.None);
    const onFriendsSortChange = (sort: ColumnSortType) => {
        setRewardSortType(ColumnSortType.None);
        setFriendsSortType(
            sort === ColumnSortType.Ascending
                ? ColumnSortType.None
                : ColumnSortType.Descending
        );
    };

    const onRewardSortChange = (sort: ColumnSortType) => {
        setFriendsSortType(ColumnSortType.None);
        setRewardSortType(
            sort === ColumnSortType.Ascending
                ? ColumnSortType.None
                : ColumnSortType.Descending
        );
    };

    useEffect(() => {
        if (friendsSortType === ColumnSortType.Descending) {
            setInviteListSortType(1);
        } else {
            setInviteListSortType(0);
        }
    }, [friendsSortType, rewardSortType]);

    if (dataList.length === 0) {
        return (
            <div className="py-10 px-3 flex flex-col items-center">
                <LottieSticker
                    lottieName="no-result"
                    className="w-[80px] h-[80px]"
                />
                <h3 className="mt-3 text-text_1 text-body1 font-semibold">
                    {t("no_reward_record")}
                </h3>
                <p className="mt-1 text-text_2 text-body3">
                    {t("no_transaction_yet")}
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-[12px] bg-bg_0 border-[0.5px] mt-2 border-border_2">
            <div className="h-[32px] px-[12px] flex items-end pb-1 justify-between text-direction1 font-Regular text-text_2">
                <div className={clsx("flex-1 w-0 flex min-w-20")}>
                    <SortedColumnHead
                        label={t("friends")}
                        sort={friendsSortType}
                        onClick={onFriendsSortChange}
                    />
                </div>
                <div className={clsx("flex-1 w-0 flex min-w-20 justify-end")}>
                    <SortedColumnHead
                        label={t("reward")}
                        sort={rewardSortType}
                        onClick={onRewardSortChange}
                    />
                </div>
            </div>

            <div>
                {dataList.map((item, index) => {
                    const noBorder = index === 0;
                    return (
                        <div
                            className="flex items-stretch cursor-pointer"
                            key={index}
                        >
                            <div className="flex-1 w-0 flex items-stretch pl-3">
                                <div className="flex-none py-3.5 mr-3">
                                    <div className="w-[36px] h-[36px]">
                                        <Avatar
                                            size={40}
                                            src={
                                                item.photo_url ||
                                                randomIcon(item.address)
                                            }
                                        />
                                    </div>
                                </div>
                                <div
                                    className={clsx(
                                        "flex-1 flex flex-col justify-center",
                                        {
                                            "border-t-[0.5px] border-border_2":
                                                !noBorder,
                                        }
                                    )}
                                >
                                    <div className="flex flex-col items-start">
                                        <div className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center ">
                                            <span
                                                className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[45vw]"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        item.user_name ||
                                                        "UNNAMED",
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <span className="text-body4 text-text_2">
                                                {formatDateOnly(
                                                    String(item.ts)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                key={index}
                                className={clsx(
                                    "flex-1 w-0 flex items-center justify-end pr-3",
                                    {
                                        "border-t-[0.5px] border-border_2":
                                            !noBorder,
                                    }
                                )}
                            >
                                <div className="flex flex-col items-end justify-center h-full">
                                    <span className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center">
                                        +{formatPrice(item.value)}
                                    </span>

                                    {Number(item.up_value) > 0 && (
                                        <span className="text-body4 text-text_1">
                                            +{formatPrice(item.up_value)}{" "}
                                            <span className="text-body4 text-text_2">
                                                {t("today")}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default InviteList;
